import { ButtonList, Icon } from '@dixa/lib-shared-habla';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { PopupMenuItem } from 'articleEditor/components/popupMenu/popupMenu';
import { Toolbar } from 'articleEditor/components/toolbar';
import cx from 'classnames';
import {
  findNodePath,
  useEditableProps,
} from 'lib-knowledge-document-structure/plate/common';
import { useMyEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import { MyTableCellElement } from 'lib-knowledge-document-structure/plateTypes';
import { useContext } from 'react';

import s from './table.module.scss';
import {
  cellPathToRowPath,
  clearColumn,
  clearRow,
  deleteTableColumn,
  deleteTableRow,
  getCanDeleteColumn,
  getCanDeleteRow,
  insertColumnAfter,
  insertColumnBefore,
  insertRowAbove,
  insertRowBelow,
} from './tableUtils';
import { TableCellHoverContext } from './useCellHover';

type TableActionButtonProps = {
  element: MyTableCellElement;
  type: 'row' | 'column';
  rowIndex: number;
  columnIndex: number;
  showMenu: boolean;
};

export function TableActionButton({
  element,
  type,
  rowIndex,
  columnIndex,
  showMenu,
}: TableActionButtonProps): JSX.Element {
  const {
    actions: { setMenuShowing, hideMenu },
  } = useContext(TableCellHoverContext);

  const { readOnly } = useEditableProps();
  if (readOnly) return <></>;

  const setShowingMenu = (isOpen: boolean) => {
    if (!isOpen) {
      hideMenu();
      return;
    }
    setMenuShowing({
      row: rowIndex,
      column: columnIndex,
      type,
    });
  };

  return (
    <DropdownMenu.Root open={showMenu} onOpenChange={setShowingMenu}>
      <DropdownMenu.Trigger
        tabIndex={-1}
        contentEditable={false}
        style={{ cursor: 'grab' }}
        className={cx(s['tableCellActionsButton'], {
          [s['tableCellColumnActionsButton']]: type === 'column',
          [s['tableCellRowActionsButton']]: type === 'row',
        })}
      >
        <Icon name="drag" size={20} />
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content asChild sideOffset={5} side="left">
          <Toolbar>
            <DropdownMenu.Group asChild>
              {type === 'column' ? (
                <ColumnActions element={element} />
              ) : (
                <RowActions element={element} />
              )}
            </DropdownMenu.Group>
          </Toolbar>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

type ColumnActionsProps = {
  element: MyTableCellElement;
};

const ColumnActions = ({ element }: ColumnActionsProps) => {
  const editor = useMyEditorRef();
  const path = findNodePath(editor, element)!;

  // Cannot delete the last cell
  const canDeleteColumn = getCanDeleteColumn({ editor, path });
  const onDeleteColumn = () => deleteTableColumn({ editor, path });
  const onAddColumnLeft = () => insertColumnBefore({ editor, element, path });
  const onAddColumnRight = () => insertColumnAfter({ editor, path });
  const onClearContent = () => clearColumn({ editor, path });

  return (
    <ButtonList>
      <PopupMenuItem
        label="Add column left"
        icon="arrow-left"
        onSelect={onAddColumnLeft}
      />
      <PopupMenuItem
        label="Add column right"
        icon="arrow-right"
        onSelect={onAddColumnRight}
      />
      {/* <PopupMenuItem label="Duplicate" icon="copy" onSelect={onDuplicate} /> */}
      <PopupMenuItem
        label="Clear content"
        icon="input"
        onSelect={onClearContent}
      />
      <PopupMenuItem
        label="Delete"
        icon="trash"
        onSelect={onDeleteColumn}
        isDisabled={!canDeleteColumn}
      />
    </ButtonList>
  );
};

type RowActionsProps = {
  element: MyTableCellElement;
};

const RowActions = ({ element }: RowActionsProps) => {
  const editor = useMyEditorRef();
  const path = findNodePath(editor, element)!;
  const rowPath = cellPathToRowPath(path);
  const canDeleteRow = getCanDeleteRow({ editor, element });

  const onDeleteRow = () => deleteTableRow({ editor, element, rowPath });
  const onAddRowAbove = () => insertRowAbove({ editor, rowPath, element });
  const onAddRowBelow = () => insertRowBelow({ editor, rowPath });
  const onClearContent = () => clearRow({ editor, rowPath });

  return (
    <ButtonList>
      <PopupMenuItem
        label="Add row above"
        icon="arrow-up"
        onSelect={onAddRowAbove}
      />
      <PopupMenuItem
        label="Add row below"
        icon="arrow-down"
        onSelect={onAddRowBelow}
      />
      {/* <PopupMenuItem label="Duplicate" icon="copy" onSelect={onDuplicate} /> */}
      <PopupMenuItem
        label="Clear content"
        icon="input"
        onSelect={onClearContent}
      />
      <PopupMenuItem
        label="Delete"
        icon="trash"
        onSelect={onDeleteRow}
        isDisabled={!canDeleteRow}
      />
    </ButtonList>
  );
};
